<template>
    <div>
        <report-header
            :title="title"
            :isShowDateRange="true"
            :formatDateRangeTitle="`(${ periodStart } - ${ periodEnd })`"
            :description="CREPORT.MESSAGE.ARR_DESCRIPTION"
        />
        <b-card v-if="isLoadingBody">
            <div id="report-body" class="w-100">
                <ArInvoiceNote
                    :show="hasReopenedInvoices"
                    @refresh="fetchData"
                />
                <Loader :margin="0" />
            </div>
        </b-card>
        <NewARR
            v-else-if="isNewARR"
            :tableData="tableData"
            :hasReopenedInvoices="hasReopenedInvoices"
            @refresh="fetchData"
        />
        <OriginalARR
            v-else
            :tableData="tableData"
            :hasReopenedInvoices="hasReopenedInvoices"
            @refresh="fetchData"
        />
    </div>
</template>

<script>
import Loader from "@/components/elements/loader";
import ArInvoiceNote from "@/components/elements/ar-note";
import { DateTime } from "luxon";
import OriginalARR from "@/components/report-billing/account-receivable-reconciliation";
import NewARR from "@/components/report-billing/account-receivable-reconciliation-new";
import AccountService from "@/services/account.js";
import Report from "@/mixins/report";
import CREPORT from "@/constants/report";

export default {
    components: {
        Loader,
        ArInvoiceNote,
        OriginalARR,
        NewARR,
        ReportHeader: () => import("@/components/report/report-header")
    },
    mixins: [Report],
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: moment(),
                    end: moment()
                };
            }
        }
    },
    data() {
        return {
            title: "Account Receivable Reconciliation",
            isLoadingBody: true,
            hasReopenedInvoices: false,
            reportData: null,
            tableData: {
                accReceivable: [],
                invoicedByProvider: [],
                payments: [],
                credits: [],
                refunds: [],
                writeoff: []
            },
            refresh: 0,
            isNewARR: true,
            CREPORT
        };
    },
    computed: {
        timestamp() {
            return DateTime.local().toFormat(this._dateFormat + " hh:mm a");
        },
        periodStart() {
            if (this.data.start) {
                return this.data.start.format(this.getDateFormat());
            }
            return null;
        },
        periodEnd() {
            if (this.data.end) {
                return this.data.end.format(this.getDateFormat());
            }
            return null;
        }
    },
    beforeDestroy: function () {
        this.$root.$off("report-filter-date-range:refresh", this.fetchData);
        this.$root.$off(
            "account-receivable-reconciliation:refresh",
            this.fetchData
        );
    },
    async mounted() {
        let self = this;
        // --- toggle navigation options
        self.data.isShowDateRange = true;

        await self._getSetting();
        self.data.start = moment().tz(self._timeZone).startOf("week");
        self.data.end = moment().tz(self._timeZone).endOf("day");
        self.data.from = moment().tz(self._timeZone).startOf("week").unix();
        self.data.to = moment().tz(self._timeZone).endOf("day").unix();
        await self.checkIsLegacyARR();
        await self.fetchData();

        self.$root.$on("report-filter-date-range:refresh", self.fetchData);
        self.$root.$on(
            "account-receivable-reconciliation:refresh",
            self.fetchData
        );

        window.scrollTo(0, 0);
        this.checkReopenedInvoices();
    },
    methods: {
        async checkIsLegacyARR() {
            let res = await AccountService.checkFeature("LEGACY_ARR");
            let isLegacy = res?.resource?.is_enabled ?? 0;

            if (isLegacy === 1) {
                this.isNewARR = false;
            }
        },
        async checkReopenedInvoices() {
            let res = await this.$http.get(
                `${process.env.BARRAMUNDI}billing/invoice`,
                {
                    params: {
                        page: 1,
                        perPage: 1,
                        status: 6
                    }
                }
            );

            let totalReopened = res?.data?.response?.meta?.total ?? 0;
            if (totalReopened > 0) {
                this.hasReopenedInvoices = true;
            }
        },
        async fetchData() {
            this.isLoadingBody = true;

            let res = await this.$http.get(
                `${process.env.BARRAMUNDI}report/reports/54`,
                {
                    params: {
                        from: this.data.from,
                        to: this.data.to,
                        refresh: new Date().getTime() // ensure always new (no-cache)
                    }
                }
            );

            this.isLoadingBody = false;
            let rawData = res.data?.response?.resource;

            if (!rawData) {
                this._message("Unable to generate report content", "danger");
                return;
            }

            // 'new' ARR
            if (this.isNewARR) {
                this.generateReportDataNew(rawData);
            }
            // original ARR
            else {
                this.generateReportDataOriginal(rawData);
            }
        },
        generateReportDataNew(raw) {
            let {
                account_receivable: ar,
                invoice_by_provider: ibp,
                payment_summary: ps,
                writeoff,
                refund,
                credits,
                credits_to_charitable_fund,
                meta,
                charitable_fund: cf
            } = raw;

            this.newGenerateAccReceivableData(ar || {});
            this.newGenerateInvoiceProviderData(ibp || []);
            this.newGeneratePaymentsData(ps || [], cf || []);
            this.newGenerateRefundsData(refund || []);
            this.newGenerateCreditsData(
                credits || [],
                credits_to_charitable_fund || []
            );
            this.title = meta.title;
            this.dynamicPageTitle(this.title);

            this.tableData.writeoff = [
                {
                    label: "Write-Offs",
                    writeoff_amount: writeoff[0].writeoff_amount || 0
                }
            ];
        },
        newGenerateAccReceivableData(raw) {
            this.tableData.accReceivable = [
                {
                    desc: "Start Balance (" + this.periodStart + ")",
                    value: raw["start_balance"] || 0
                },
                {
                    desc: "Ending Balance (" + this.periodEnd + ")",
                    value: raw["end_balance"] || 0
                },
                {
                    isTotal: true,
                    desc: "Change in Accounts Receivable",
                    value: raw["change"] || 0
                }
            ];
        },
        newGenerateInvoiceProviderData(raw) {
            // loop to calculate grand total
            let totalRow = {
                provider: "Total Invoiced",
                list_price: 0,
                discount: 0,
                tax: 0,
                late_fee: 0,
                total: 0,
                isTotal: true
            };

            for (let record of raw) {
                totalRow.list_price += Number(record.list_price);
                totalRow.discount += Number(record.discount);
                totalRow.tax += Number(record.tax);
                totalRow.late_fee += Number(record.late_fee);
                totalRow.total += Number(record.total);
            }

            this.tableData.invoicedByProvider = [...raw, totalRow];
        },
        newGeneratePaymentsData(raw, cf) {
            let tooltip = "";
            let totalRow = {
                payment_name: "Total Payments",
                payment_amount: 0,
                isTotal: true
            };

            let arrNames = [];
            for (let record of raw) {
                arrNames.push(record.payment_name);
                totalRow.payment_amount += Number(record.payment_amount);
            }
            tooltip = arrNames.join(" + ");

            let charitableFundRows = [];
            let charitableFundAmount = 0;

            for (let cfund of cf) {
                let amount = cfund?.amount ?? 0;
                amount = Number(amount);

                if (amount > 0) {
                    let name = cfund.fund_type;

                    // if non-applied charitable fund
                    if (!name.toLowerCase().includes("applied")) {
                        amount = amount * -1;
                        tooltip += " - " + name;
                    } else {
                        tooltip += " + " + name;
                    }

                    charitableFundAmount += amount;
                    charitableFundRows.push({
                        payment_name: cfund.fund_type,
                        payment_amount: amount,
                        isExtendedTotal: false
                    });
                }
            }

            this.tableData.payments = [
                ...raw,
                totalRow,
                ...charitableFundRows,
                {
                    payment_name: "¹Total applicable payments for change in AR",
                    payment_amount: totalRow.payment_amount + charitableFundAmount, // prettier-ignore
                    isExtendedTotal: true,
                    htmlId: "payment",
                    tooltip: tooltip
                }
            ];
        },
        newGenerateCreditsData(raw, creditsToCharitableFund) {
            let tooltip = "";
            let totalApplicableCreditAmount = 0;
            let outOfPeriodAmount = 0;
            let outOfPeriodRow = {};
            let totalRow = {
                credit_type: "Total Credits",
                credit_amount: 0,
                isTotal: true
            };

            let arrName = [];
            for (let record of raw) {
                if (record.credit_type.toLowerCase().includes("period")) {
                    outOfPeriodRow = {
                        credit_type: record.credit_type,
                        credit_amount: -Math.abs(record.credit_amount),
                        isExtendedTotal: false
                    };
                    outOfPeriodAmount = Number(record.credit_amount);
                    continue;
                }

                if (record.credit_applicable === 1) {
                    totalApplicableCreditAmount += Number(record.credit_amount);
                    arrName.push(record.credit_type);
                }

                if (record.credit_type == "Applied") {
                    // always negative
                    totalRow.credit_amount -= Number(record.credit_amount);
                    record.credit_amount = -Math.abs(record.credit_amount);
                } else {
                    totalRow.credit_amount += Number(record.credit_amount);
                }
            }

            let charitableFundRow = [];
            for (let cfund of creditsToCharitableFund) {
                let amount = cfund?.amount ?? 0;
                amount = Number(amount);

                if (amount > 0) {
                    totalApplicableCreditAmount -= Number(amount);

                    amount = Math.abs(amount) * -1;

                    charitableFundRow.push({
                        credit_type: cfund.fund_type,
                        credit_amount: amount,
                        isExtendedTotal: false
                    });
                }
            }

            tooltip = arrName.join(" + ");

            let rowOutOfPeriod = [];
            if (outOfPeriodAmount) {
                rowOutOfPeriod.push(outOfPeriodRow);
                if (tooltip === "") {
                    tooltip += outOfPeriodRow.credit_type;
                } else {
                    tooltip += " - " + outOfPeriodRow.credit_type;
                }
            }

            this.tableData.credits = [
                ...raw,
                totalRow,
                ...rowOutOfPeriod,
                ...charitableFundRow,
                {
                    credit_type: "²Total applicable credits for change in AR",
                    credit_amount:
                        totalApplicableCreditAmount - outOfPeriodAmount,
                    isExtendedTotal: true,
                    htmlId: "credit",
                    tooltip: tooltip
                }
            ];
        },
        newGenerateRefundsData(raw) {
            let tooltip = "";
            let totalRow = {
                refund_type: "Total Refunds",
                refund_amount: 0,
                isTotal: true
            };

            let amountOthers = 0;

            for (let record of raw) {
                if (record.refund_applicable === 1) {
                    amountOthers += Number(record.refund_amount);
                    tooltip = record.refund_type;
                }
                totalRow.refund_amount += Number(record.refund_amount);
            }

            this.tableData.refunds = [
                ...raw,
                totalRow,
                {
                    refund_type: "³Total applicable refunds for change in AR",
                    refund_amount: amountOthers,
                    isExtendedTotal: true,
                    htmlId: "refund",
                    tooltip: tooltip
                }
            ];
        },
        generateReportDataOriginal(raw) {
            let {
                account_receivable: ar,
                invoice_by_provider: ibp,
                payment_summary: ps,
                writeoff,
                refund,
                credits,
                meta
            } = raw;

            this.oldGenerateAccReceivableData(ar || {});
            this.oldGenerateInvoiceProviderData(ibp || []);
            this.oldGeneratePaymentsData(ps || []);
            this.oldGenerateCreditsData(credits || []);
            this.title = meta.title;
            this.dynamicPageTitle(this.title);

            this.tableData.refunds = [
                {
                    label: "Refunds",
                    refund_amount: refund[0].refund_amount || 0
                }
            ];

            this.tableData.writeoff = [
                {
                    label: "Write-Offs",
                    writeoff_amount: writeoff[0].writeoff_amount || 0
                }
            ];
        },
        oldGenerateAccReceivableData(raw) {
            this.tableData.accReceivable = [
                {
                    desc: "Start Balance (" + this.periodStart + ")",
                    value: raw["start_balance"] || 0
                },
                {
                    desc: "Ending Balance (" + this.periodEnd + ")",
                    value: raw["end_balance"] || 0
                },
                {
                    isTotal: true,
                    desc: "Change in Accounts Receivable",
                    value: raw["change"] || 0
                }
            ];
        },
        oldGenerateInvoiceProviderData(raw) {
            // loop to calculate grand total
            let totalRow = {
                provider: "Total Invoiced",
                list_price: 0,
                discount: 0,
                tax: 0,
                late_fee: 0,
                total: 0,
                isTotal: true
            };

            for (let record of raw) {
                totalRow.list_price += Number(record.list_price);
                totalRow.discount += Number(record.discount);
                totalRow.tax += Number(record.tax);
                totalRow.late_fee += Number(record.late_fee);
                totalRow.total += Number(record.total);
            }

            this.tableData.invoicedByProvider = [...raw, totalRow];
        },
        oldGeneratePaymentsData(raw) {
            let totalRow = {
                payment_name: "Total Payments",
                payment_amount: 0,
                isTotal: true
            };

            for (let record of raw) {
                totalRow.payment_amount += Number(record.payment_amount);
            }

            this.tableData.payments = [...raw, totalRow];
        },
        oldGenerateCreditsData(raw) {
            let totalRow = {
                credit_type: "Total Credits",
                credit_amount: 0,
                isTotal: true
            };

            for (let record of raw) {
                if (record.credit_type == "Applied") {
                    // always negative
                    totalRow.credit_amount -= Number(record.credit_amount);
                    record.credit_amount = -Math.abs(record.credit_amount);
                } else {
                    totalRow.credit_amount += Number(record.credit_amount);
                }
            }

            this.tableData.credits = [...raw, totalRow];
        }
    }
};
</script>
